import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronRight, CircleAlert, CircleCheckBig, CircleX, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchCurrentUserBookings } from '../lib/bookingService';
import {
  EVENT_STATUS_FILTER_ALL,
  EVENT_STATUS_FILTER_OPTIONS,
  filterClientBookings,
  getFinalStageLabel,
  isConcertBookingType,
  resolveClientListStage,
} from '../lib/bookingListFilters';
import { supabase } from '../lib/supabaseClient';
import BookingPagination from '../components/BookingPagination';
import { BusyTable } from '../components/TableBusyOverlay';
import usePagedListUi from '../hooks/usePagedListUi';

const PAGE_SIZE = 5;

const statusConfig = {
  pending_review: {
    label: 'Pending',
    className: 'bg-orange-500 text-white',
    icon: CircleAlert,
  },
  admin_review: {
    label: 'Pending',
    className: 'bg-orange-500 text-white',
    icon: CircleAlert,
  },
  request_submitted: {
    label: 'Submitted',
    className: 'bg-blue-600 text-white',
    icon: CircleAlert,
  },
  initial_consultation: {
    label: 'Initial Consultation',
    className: 'bg-sky-600 text-white',
    icon: CircleAlert,
  },
  proposal_contract: {
    label: 'Proposal & Contract',
    className: 'bg-violet-600 text-white',
    icon: CircleAlert,
  },
  proposal_quotation: {
    label: 'Proposal & Contract',
    className: 'bg-violet-600 text-white',
    icon: CircleAlert,
  },
  agreement_confirmation: {
    label: 'Proposal & Contract',
    className: 'bg-violet-600 text-white',
    icon: CircleAlert,
  },
  contract_signing: {
    label: 'Proposal & Contract',
    className: 'bg-violet-600 text-white',
    icon: CircleAlert,
  },
  event_preparation: {
    label: 'Event Preparation',
    className: 'bg-fuchsia-600 text-white',
    icon: CircleAlert,
  },
  accepted: {
    label: 'Event Preparation',
    className: 'bg-violet-600 text-white',
    icon: CircleAlert,
  },
  event_live: {
    label: 'Event is Now Live!',
    className: 'bg-emerald-600 text-white',
    icon: CircleCheckBig,
  },
  declined: {
    label: 'Rejected',
    className: 'bg-red-600 text-white',
    icon: CircleX,
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-slate-500 text-white',
    icon: CircleX,
  },
};

const resolveDisplayStatus = (booking) => resolveClientListStage(booking);

const formatBookingDate = (booking) => {
  const source = booking?.event_date || booking?.created_at;
  if (!source) return 'Pending Date';

  const parsed = new Date(source);
  if (Number.isNaN(parsed.getTime())) return 'Pending Date';

  return parsed.toLocaleDateString('en-PH', { timeZone: 'Asia/Manila', month: 'long', day: 'numeric', year: 'numeric' });
};

const getStatusBadgeLabel = (booking, statusKey, config) => {
  if (statusKey === 'event_live') return getFinalStageLabel(booking);
  return config.label;
};

const getEventTypeLabel = (booking) => (isConcertBookingType(booking) ? 'Concert' : 'Corporate');

export default function BookingsListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);
  const [showConcert, setShowConcert] = useState(true);
  const [showCorporate, setShowCorporate] = useState(true);
  const [eventStatus, setEventStatus] = useState(EVENT_STATUS_FILTER_ALL);
  const subscriptions = React.useRef({});

  const refreshBookings = async () => {
    try {
      const { bookings: userBookings } = await fetchCurrentUserBookings();
      const normalized = (userBookings || []).map((booking) => ({
        ...booking,
        displayStatus: resolveDisplayStatus(booking),
      }));
      setBookings(normalized);

      Object.values(subscriptions.current || {}).forEach((sub) => sub.unsubscribe());
      subscriptions.current = {};

      normalized.forEach((booking) => {
        if (!booking?.id) return;

        const channel = supabase
          .channel(`booking-list:${booking.id}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'bookings',
              filter: `id=eq.${booking.id}`,
            },
            (payload) => {
              if (payload.new) {
                const updated = {
                  ...payload.new,
                  displayStatus: resolveDisplayStatus(payload.new),
                };
                setBookings((prev) =>
                  prev.map((b) => (b.id === booking.id ? updated : b))
                );
              }
            }
          )
          .subscribe();

        subscriptions.current[booking.id] = channel;
      });
    } catch (error) {
      console.error('Error fetching bookings list:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshBookings();

    return () => {
      Object.values(subscriptions.current || {}).forEach((sub) => sub.unsubscribe());
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      refreshBookings();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshBookings();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const filteredBookings = useMemo(
    () => filterClientBookings(bookings, {
      concert: showConcert,
      corporate: showCorporate,
      eventStatus,
    }),
    [bookings, showConcert, showCorporate, eventStatus]
  );

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [showConcert, showCorporate, eventStatus]);

  useEffect(() => {
    setCurrentPage((previousPage) => Math.min(previousPage, totalPages));
  }, [totalPages]);

  const visibleBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredBookings.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredBookings, currentPage]);

  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  const toggleClass = (active) => (
    active
      ? 'bg-[#a20f0f] text-white border-[#a20f0f]'
      : 'bg-black/70 text-slate-300 border-white/10 hover:text-white'
  );

  return (
    <div className="text-white">
      <div className="mx-auto w-full max-w-[360px] px-4 pt-1 pb-4 md:max-w-[420px]">
        <h1 className="text-2xl md:text-3xl font-bold text-center text-white mb-4">Event Booking</h1>

        {!loading && bookings.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-pressed={showConcert}
              onClick={() => setShowConcert((value) => !value)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${toggleClass(showConcert)}`}
            >
              Concert
            </button>
            <button
              type="button"
              aria-pressed={showCorporate}
              onClick={() => setShowCorporate((value) => !value)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${toggleClass(showCorporate)}`}
            >
              Corporate
            </button>
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-300">
              <span className="whitespace-nowrap">Event Status:</span>
              <select
                value={eventStatus}
                onChange={(event) => setEventStatus(event.target.value)}
                className="rounded-lg border border-white/10 bg-black/70 py-1.5 pl-2 pr-7 text-xs font-semibold leading-none text-white"
              >
                {EVENT_STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {loading && bookings.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/70 px-4 py-8 text-center text-sm text-slate-400">
            Loading...
          </div>
        ) : bookings.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/70 px-4 py-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/10">
              <CalendarDays size={26} className="text-white" />
            </div>
            <p className="text-xl font-bold text-white mb-2">No Bookings Yet</p>
            <p className="text-sm text-slate-400 mb-6">Start planning your next event with us.</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/70 px-4 py-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/10">
              <CalendarDays size={26} className="text-white" />
            </div>
            <p className="text-xl font-bold text-white mb-2">No Matching Bookings</p>
            <p className="text-sm text-slate-400">Try a different type or event status filter.</p>
          </div>
        ) : (
          <div ref={listTopRef} className="space-y-4">
            <BusyTable show={showSkeleton} tone="public">
            {visibleBookings.map((booking) => {
              const statusKey = booking.displayStatus in statusConfig ? booking.displayStatus : 'pending_review';
              const config = statusConfig[statusKey];
              const Icon = config.icon;
              const badgeLabel = getStatusBadgeLabel(booking, statusKey, config);
              const eventTypeLabel = getEventTypeLabel(booking);

              return (
                <button
                  key={booking.id}
                  type="button"
                  onClick={() => navigate(`/book/progress/${booking.id}`)}
                  className="w-full rounded-2xl border border-white/10 bg-black/80 px-4 py-3.5 shadow-lg hover:border-white/30 hover:bg-black/90 transition text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-full bg-white/5 border border-white/10 shrink-0">
                      <CalendarDays size={20} className="text-white" />
                    </div>

                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate text-sm font-bold text-white">{booking.event_name || 'Event Title'}</p>
                      <p className="truncate text-xs text-slate-400">{formatBookingDate(booking)}</p>
                      <p className="truncate text-xs text-slate-300">{eventTypeLabel}</p>
                      <div className={`mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${config.className}`}>
                        <Icon size={12} className="shrink-0" />
                        <span className="truncate">{badgeLabel}</span>
                      </div>
                      {statusKey === 'declined' && booking.decline_reason ? (
                        <p className="mt-1.5 line-clamp-2 text-xs text-slate-400">{booking.decline_reason}</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-end">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#b31a1a]">
                      View Booking
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </button>
              );
            })}
            </BusyTable>

            <BookingPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        )}

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => navigate('/book/select')}
            className="inline-flex min-w-[190px] items-center justify-center gap-2 rounded-xl bg-[#a20f0f] px-5 py-3 text-sm font-semibold text-white hover:bg-[#8d0d0d] transition"
          >
            <Plus size={16} /> Create Booking
          </button>
        </div>
      </div>
    </div>
  );
}
